// Pure helpers of PageTree (kept apart so the component file only exports components).

/** One page (or folder-like page) of the tree. Callers map their models to it; ids are strings. */
export interface PageTreeNode {
    id: string;
    title: string;
    /** `undefined`: a leaf. An empty array still renders as a leaf (Ark pages have no folder kind). */
    children?: PageTreeNode[];
    /** Unchanged ancestor shown for context only (the change-request tree): dimmed, not selectable. */
    context?: boolean;
    /** Struck through (a page deleted by a change request). */
    struck?: boolean;
}

/** The nodes whose title (or a descendant's) contains `search`, ancestors kept so the matches stay in place. */
export function filterPageTree(nodes: PageTreeNode[], search: string): PageTreeNode[] {
    const needle = search.trim().toLowerCase();
    if (!needle) return nodes;
    return nodes.flatMap(node => {
        const children = node.children ? filterPageTree(node.children, search) : [];
        return node.title.toLowerCase().includes(needle) || children.length ? [{ ...node, children }] : [];
    });
}

/** Ids of the ancestors of `id` (root first), or `undefined` when it is not in the tree. */
export function pathToNode(nodes: PageTreeNode[], id: string, trail: string[] = []): string[] | undefined {
    for (const node of nodes) {
        if (node.id === id) return trail;
        if (node.children) {
            const found = pathToNode(node.children, id, [...trail, node.id]);
            if (found) return found;
        }
    }
    return undefined;
}

export function allIds(nodes: PageTreeNode[], out: string[] = []): string[] {
    for (const node of nodes) {
        if (node.children && node.children.length) {
            out.push(node.id);
            allIds(node.children, out);
        }
    }
    return out;
}

export function findNode(nodes: PageTreeNode[], id: string): PageTreeNode | undefined {
    for (const node of nodes) {
        if (node.id === id) return node;
        const found = node.children ? findNode(node.children, id) : undefined;
        if (found) return found;
    }
    return undefined;
}

export function siblingsOf(nodes: PageTreeNode[], id: string): PageTreeNode[] | undefined {
    if (nodes.some(node => node.id === id)) return nodes;
    for (const node of nodes) {
        const found = node.children ? siblingsOf(node.children, id) : undefined;
        if (found) return found;
    }
    return undefined;
}

export function isInSubtree(nodes: PageTreeNode[], ancestorId: string, candidateId: string): boolean {
    const ancestor = findNode(nodes, ancestorId);
    return !!ancestor && (ancestor.id === candidateId || !!(ancestor.children && findNode(ancestor.children, candidateId)));
}
